// What is selected in the model: the drawing's objects, by kind, and at most ONE
// of a room's features. Every rule of the selection's shape is here:
//  - each kind of drawing object (`ItemKind`) has its slot (`SLOTS`): a primary id
//    and, for the kinds a selection can hold several of, the list (the primary is
//    the last picked; a list of one is the primary alone). A new kind does not
//    compile until it has one, so clearing, replacing and pruning reach it;
//  - a room's feature (a bench, a partition, a pane, a backrest, …) is ONE value:
//    picking one replaces whatever feature was selected, so two can never be
//    half-selected at once, whichever order the clicks come in; it is kept
//    while its room holds it (roomFeatures.ts `liveFeature`, the one resolution).
//  - beside them, what is selected on its own (a view's title, a constraint) and a
//    part of the one selected shape (a polygon's edge, a drawn wall's door or
//    pane, a floor's drain) live here too, so selecting anything lets go of them:
//    a press never lists what it drops, it names what it selects (`selectOne`,
//    `selectObjects`, `selectViewTitle`, `selectConstraint`, `selectPolyEdge`,
//    `selectWallFeature`, `selectFloorDrain`, `selectFeature`);
//  - "the selected objects" is ONE query over ItemKind: their ids by kind
//    (`selectedOf`, `selectedObjects`), how many (`selectedCount`), the objects
//    themselves (`selectedObjectsOf`, `selectedItems`); a selection to set is an
//    `ObjectSel`. A caller names the kinds it means (`MULTI_KINDS`,
//    `TRANSFORM_KINDS`) instead of spelling seven of them out.
// DOM-free: main.ts's `state` holds the fields, and `exists` / `gone` answers come
// from the caller.

import {
  type ArrayKey,
  type KindObjects,
  type ObjectAdapter,
  objectsOf,
  type ObjItem,
} from './drawingObjects';
import type { ItemKind } from './layers';
import type { ProductInstance } from '../shared/types';
import {
  featureRoomId,
  liveFeature,
} from './roomFeatures';

/** A room's feature selected — at most one kind at a time. A door, panes and
 *  openings belong to the selected room (`state.selectedId`); the rest name
 *  their room. Panes and openings are picked several at a time (`ids`, `id` the
 *  last picked); a light picked with others (`picks`) is edited with them. */
export type FeatureSel =
  | { kind: 'bench'; instId: string; benchId: string }
  | { kind: 'door'; id: string }
  | { kind: 'fixture'; instId: string; id: string; picks?: string[] }
  | { kind: 'glass'; id: string | null; ids: string[] }
  | { kind: 'heaterWall'; instId: string; wallId: string }
  | { kind: 'innerWall'; instId: string; wallId: string }
  | { kind: 'opening'; id: string | null; ids: string[] }
  | { kind: 'support'; instId: string; id: string; run?: number };
export type FeatureKind = FeatureSel['kind'];
/** A view's title, selected on its own (its view not). */
export type ViewTitleRef = { kind: 'section' | 'rcp'; id: string };
type FeatureOf<K extends FeatureKind> = Extract<FeatureSel, { kind: K }>;

/** The fields of main.ts's `state` that hold the selection. */
export interface SelectionFields {
  selectedId: string | null;
  selectedIds: string[];
  selectedPolyId: string | null;
  selectedPolyIds: string[];
  selectedWallId: string | null;
  selectedWallIds: string[];
  selectedTextId: string | null;
  selectedTextIds: string[];
  selectedArcId: string | null;
  selectedArcIds: string[];
  selectedLeaderId: string | null;
  selectedLeaderIds: string[];
  selectedRevCloudId: string | null;
  selectedRevCloudIds: string[];
  selectedDimId: string | null;
  selectedConnId: string | null;
  selectedSectionId: string | null;
  selectedRcpId: string | null;
  selectedFloorId: string | null;
  selectedScheduleTable: string | null;
  feature: FeatureSel | null;
  /** A view's title on its own. */
  selectedViewTitle: ViewTitleRef | null;
  /** A constraint (its badge, or its row in the list). */
  selectedConstraintId: string | null;
  /** One edge of the selected polygon (vertex `edge` to `edge` + 1). */
  selectedPolyEdge: { id: string; edge: number } | null;
  /** A door, pane or opening of the selected drawn wall. */
  selectedWallFeatureId: string | null;
  /** The selected floor's drain. */
  selectedFloorDrain: boolean;
}

type OneField = { [K in keyof SelectionFields]: SelectionFields[K] extends string | null ? K : never }[keyof SelectionFields];
type ManyField = { [K in keyof SelectionFields]: SelectionFields[K] extends string[] ? K : never }[keyof SelectionFields];

/** Where each kind's selection is held. A schedule table's is its table key, not an id. */
const SLOT_TABLE = {
  arc: { one: 'selectedArcId', many: 'selectedArcIds' },
  cloud: { one: 'selectedRevCloudId', many: 'selectedRevCloudIds' },
  conn: { one: 'selectedConnId', many: null },
  dim: { one: 'selectedDimId', many: null },
  flooring: { one: 'selectedFloorId', many: null },
  instance: { one: 'selectedId', many: 'selectedIds' },
  leader: { one: 'selectedLeaderId', many: 'selectedLeaderIds' },
  polygon: { one: 'selectedPolyId', many: 'selectedPolyIds' },
  rcp: { one: 'selectedRcpId', many: null },
  section: { one: 'selectedSectionId', many: null },
  table: { one: 'selectedScheduleTable', many: null },
  text: { one: 'selectedTextId', many: 'selectedTextIds' },
  wall: { one: 'selectedWallId', many: 'selectedWallIds' },
} as const satisfies Record<ItemKind, { one: OneField; many: ManyField | null }>;
export const SLOTS: Record<ItemKind, { one: OneField; many: ManyField | null }> = SLOT_TABLE;
export const SELECTION_KINDS = Object.keys(SLOTS) as ItemKind[];
/** The kinds a selection can hold several of, as a type (what `MULTI_KINDS`
 *  lists): a Record or switch over it (a group's members, Copy colours'
 *  targets) does not compile until a new one says how it is handled. */
export type MultiKind = { [K in ItemKind]: (typeof SLOT_TABLE)[K]['many'] extends null ? never : K }[ItemKind];
/** The kinds a selection can hold several of (a marquee, Shift+click, Select All). */
export const MULTI_KINDS = SELECTION_KINDS.filter((k): k is MultiKind => SLOTS[k].many != null);

/** The selected ids of one kind: its list, else its primary alone, else none. */
export function selectedOf(s: SelectionFields, kind: ItemKind): string[] {
  const { one, many } = SLOTS[kind];
  const list = many ? s[many] : [];
  if (list.length) return list;
  const id = s[one];
  return id ? [id] : [];
}

/** The kinds a transform acts on (Move, Rotate, Scale, Mirror, the arrays, a
 *  copy by a typed offset): the multi kinds and a selected dimension. */
export const TRANSFORM_KINDS: readonly ItemKind[] = [...MULTI_KINDS, 'dim'];

/** The rows of each kind's adapter (drawingObjects.ts) the selection's verbs
 *  read: how Delete takes it (`remove`) and how Copy does (`copy`). */
export type SelectionVerbs = { [K in ItemKind]: Pick<ObjectAdapter<KindObjects[K]>, 'remove' | 'copy'> };

/** The ids Delete takes of the selection: every selected object of every kind
 *  Delete takes (its adapter's `remove`; a schedule table's is null) — a
 *  dimension, a floor or a section a paste selected beside parts goes with them. */
export function deleteTargets(s: SelectionFields, A: SelectionVerbs): string[] {
  return SELECTION_KINDS.filter((k) => A[k].remove).flatMap((k) => selectedOf(s, k));
}

/** Whether Copy takes anything of the selection: an object of a kind copied by
 *  its selection (a lone dimension too); a connection is copied only with what
 *  it joins, a schedule table never. Cut, Copy and Duplicate ask it. */
export function selectionCopies(s: SelectionFields, A: SelectionVerbs): boolean {
  return SELECTION_KINDS.some((k) => A[k].copy?.take.by === 'selection' && selectedOf(s, k).length > 0);
}

/** A selection by kind: what `selectObjects` takes and `selectedObjects` answers. */
export type ObjectSel = Partial<Record<ItemKind, readonly string[]>>;

/** The selection's ids by kind, of these kinds (every kind by default). */
export function selectedObjects(s: SelectionFields, kinds: readonly ItemKind[] = SELECTION_KINDS): Partial<Record<ItemKind, string[]>> {
  return Object.fromEntries(kinds.map((k) => [k, selectedOf(s, k)]));
}

/** How many objects of these kinds are selected (every kind by default; a
 *  feature counts as its room). */
export function selectedCount(s: SelectionFields, kinds: readonly ItemKind[] = SELECTION_KINDS): number {
  return kinds.reduce((n, k) => n + selectedOf(s, k).length, 0);
}

/** The selected objects of one kind, in the drawing's order. */
export function selectedObjectsOf<K extends ItemKind>(s: SelectionFields, d: Partial<Record<ArrayKey, unknown>>, kind: K): KindObjects[K][] {
  const ids = new Set(selectedOf(s, kind));
  if (!ids.size) return [];
  return (objectsOf(d, kind) ?? []).filter((o) => ids.has((o as { id: string }).id));
}

/** The objects a selection names, kind by kind in its own order, each kind's in the
 *  drawing's order — as the adapters take them (drawingObjects.ts `ObjItem`). */
export function itemsOf(d: Partial<Record<ArrayKey, unknown>>, sel: ObjectSel): ObjItem[] {
  return (Object.keys(sel) as ItemKind[]).flatMap((kind) => {
    const ids = new Set(sel[kind]);
    if (!ids.size) return [];
    return (objectsOf(d, kind) ?? []).filter((o) => ids.has((o as { id: string }).id)).map((obj) => ({ kind, obj }) as ObjItem);
  });
}

/** The selected objects of these kinds as the adapters take them (what a transform
 *  acts on by default). */
export function selectedItems(s: SelectionFields, d: Partial<Record<ArrayKey, unknown>>, kinds: readonly ItemKind[] = TRANSFORM_KINDS): ObjItem[] {
  return itemsOf(d, selectedObjects(s, kinds));
}

/** The objects of these kinds that `ids` name, each under its kind. */
export function selectionOfIds(d: Partial<Record<ArrayKey, unknown>>, ids: Iterable<string>, kinds: readonly ItemKind[] = MULTI_KINDS): Partial<Record<ItemKind, string[]>> {
  const want = new Set(ids);
  return Object.fromEntries(kinds.map((k) => [k, (objectsOf(d, k) ?? []).map((o) => (o as { id: string }).id).filter((id) => want.has(id))]));
}

/** `b` joined to `a` (each id once, `a`'s first), or — `remove` — taken out of it. */
export function mergeSelections(a: ObjectSel, b: ObjectSel, remove = false): Partial<Record<ItemKind, string[]>> {
  const out: Partial<Record<ItemKind, string[]>> = {};
  for (const k of SELECTION_KINDS) {
    const x = a[k] ?? [], y = new Set(b[k] ?? []);
    if (!x.length && !y.size) continue;
    out[k] = remove ? x.filter((id) => !y.has(id)) : [...new Set([...x, ...y])];
  }
  return out;
}

function setSlot(s: SelectionFields, kind: ItemKind, ids: readonly string[]): void {
  const { one, many } = SLOTS[kind];
  (s[one] as string | null) = ids[ids.length - 1] ?? null;
  if (many) s[many] = [...ids];
}

/** What rides on the selection beside its objects let go: the feature, a title
 *  or a constraint on its own, a part of a shape. */
function dropAside(s: SelectionFields): void {
  s.feature = null;
  s.selectedViewTitle = null;
  s.selectedConstraintId = null;
  s.selectedPolyEdge = null;
  s.selectedWallFeatureId = null;
  s.selectedFloorDrain = false;
}

/** Nothing selected: no object of any kind, no feature, no title, no
 *  constraint, no part of a shape. */
export function clearSelection(s: SelectionFields): void {
  for (const k of SELECTION_KINDS) setSlot(s, k, []);
  dropAside(s);
}

/** Exactly these objects selected (everything else let go). */
export function selectObjects(s: SelectionFields, sel: ObjectSel): void {
  clearSelection(s);
  for (const k of SELECTION_KINDS) if (sel[k]?.length) setSlot(s, k, sel[k]!);
}

/** This object alone selected (a table's slot takes its table key). */
export function selectOne(s: SelectionFields, kind: ItemKind, id: string): void {
  selectObjects(s, { [kind]: [id] });
}

/** A room alone selected, keeping what is picked of it when it was the room
 *  selected already (its feature: a bench, a light, a support …) — a room tool's
 *  press that takes one fixture away, swaps a corner or starts a drag on a wall
 *  leaves the rest of the room's selection as it was. */
export function selectRoomKeepingFeature(s: SelectionFields, roomId: string): void {
  const keep = s.selectedId === roomId ? s.feature : null;
  selectOne(s, 'instance', roomId);
  s.feature = keep;
}

/** One object added to the selection, or — selected already — let go. The other
 *  kinds stay (a selection mixes them); a feature, a title, a constraint and a
 *  part of a shape do not. */
export function toggleObject(s: SelectionFields, kind: ItemKind, id: string): void {
  const ids = new Set(selectedOf(s, kind));
  if (ids.has(id)) ids.delete(id); else ids.add(id);
  setSlot(s, kind, SLOTS[kind].many ? [...ids] : ids.has(id) ? [id] : []);
  dropAside(s);
}

/** A view's title selected on its own. */
export function selectViewTitle(s: SelectionFields, t: ViewTitleRef): void {
  clearSelection(s);
  s.selectedViewTitle = t;
}

/** A constraint selected on its own. */
export function selectConstraint(s: SelectionFields, id: string): void {
  clearSelection(s);
  s.selectedConstraintId = id;
}

/** A polygon alone selected, and one of its edges. */
export function selectPolyEdge(s: SelectionFields, polyId: string, edge: number): void {
  selectOne(s, 'polygon', polyId);
  s.selectedPolyEdge = { id: polyId, edge };
}

/** A drawn wall alone selected, and one of its doors / panes / openings (or none). */
export function selectWallFeature(s: SelectionFields, wallId: string, featureId: string | null): void {
  selectOne(s, 'wall', wallId);
  s.selectedWallFeatureId = featureId;
}

/** A floor alone selected, and its drain. */
export function selectFloorDrain(s: SelectionFields, floorId: string): void {
  selectOne(s, 'flooring', floorId);
  s.selectedFloorDrain = true;
}

/** Keep each kind's list in step with its primary and drop what no longer
 *  exists: a primary cleared (another kind picked by a single-select setter)
 *  empties its list, an id `exists` denies drops out, and a room's feature goes
 *  once it is stale against `rooms` (roomFeatures.ts `liveFeature`). */
export function reconcileSelection(s: SelectionFields, exists: (kind: ItemKind, id: string) => boolean, rooms: readonly ProductInstance[]): void {
  for (const k of SELECTION_KINDS) {
    const { one, many } = SLOTS[k];
    const primary = s[one];
    if (primary && !exists(k, primary)) (s[one] as string | null) = null;
    if (!many) continue;
    const p = s[one];
    const live = s[many].filter((id) => exists(k, id));
    s[many] = !p ? [] : live.length <= 1 || !live.includes(p) ? [p] : live;
  }
  // A room's feature goes once its room no longer holds it, or is not the one
  // selected (a bench picked alone: or once another is).
  if (s.feature && !liveFeature(s, rooms)) s.feature = null;
  // A part of a shape goes with its shape.
  if (s.selectedPolyEdge && s.selectedPolyEdge.id !== s.selectedPolyId) s.selectedPolyEdge = null;
  if (s.selectedWallFeatureId && !s.selectedWallId) s.selectedWallFeatureId = null;
  if (s.selectedFloorDrain && !s.selectedFloorId) s.selectedFloorDrain = false;
}

/** Let go of every selected object `gone` says is no longer pickable (a hidden or
 *  locked layer), and of the feature with its room. */
export function dropFromSelection(s: SelectionFields, gone: (id: string) => boolean): void {
  for (const k of SELECTION_KINDS) {
    const { one, many } = SLOTS[k];
    if (k !== 'table' && s[one] && gone(s[one]!)) (s[one] as string | null) = null;
    if (many) s[many] = s[many].filter((id) => !gone(id));
  }
  const room = featureRoomId(s);
  if (s.feature && (!room || gone(room))) s.feature = null;
}

// ---- A room's feature -----------------------------------------------------------

/** The feature selected, if it is of this kind. */
export function featureOf<K extends FeatureKind>(s: Pick<SelectionFields, 'feature'>, kind: K): FeatureOf<K> | null {
  return s.feature?.kind === kind ? (s.feature as FeatureOf<K>) : null;
}

/** This feature selected — whatever feature was before is let go. */
export function selectFeature(s: Pick<SelectionFields, 'feature'>, f: FeatureSel): void {
  s.feature = f;
}

/** The feature let go — kinds given, only if it is one of them. */
export function dropFeature(s: Pick<SelectionFields, 'feature'>, ...kinds: FeatureKind[]): void {
  if (s.feature && (!kinds.length || kinds.includes(s.feature.kind))) s.feature = null;
}
